import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Footer } from "./Footer";
import { JitsiRoom } from "./JitsiRoom";
import { PushButton } from "./PushButton";
import { useWaitingRing } from "../lib/waitingRing";
import { fmtAppt, fmtStamp } from "../lib/datetime";
import { AccountArea } from "./AccountArea";
import { LanguageFields } from "./LanguageFields";
import { STOCK, INV_ICON } from "../lib/categories";
import { toast } from "sonner";
import {
  HeartPulse, LogOut, ExternalLink, UserCog, CalendarClock, Video,
  Plus, Trash2, Loader2, Check, Upload, Stethoscope, MessageSquare, Send, Package, MapPin, Settings,
} from "lucide-react";

const TABS = [
  { k: "profile", label: "My Profile", icon: UserCog },
  { k: "availability", label: "My Availability", icon: CalendarClock },
  { k: "appointments", label: "My Consultations", icon: Video },
  { k: "inventory", label: "Inventory", icon: Package },
  { k: "messages", label: "Inbox", icon: MessageSquare },
  { k: "settings", label: "Settings", icon: Settings },
];
const inp = "w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function InventoryTab() {
  const [items, setItems] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [inv, cl] = await Promise.all([api.get("/inventory"), api.get("/clinics")]);
        setItems(inv.data); setClinics(cl.data);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  const clinicName = (id) => clinics.find((c) => c.id === id)?.name || "";
  if (loading) return <div className="text-muted2 text-sm py-8">Loading inventory…</div>;
  return (
    <section data-testid="clinician-inventory">
      <h2 className="font-heading font-bold text-2xl tracking-tight text-ink flex items-center gap-3">
        <Package className="w-6 h-6 text-clinical" /> Medication &amp; Blood Stock
      </h2>
      <p className="text-muted2 mt-1 mb-6 text-sm">Live availability for time-critical treatments across clinics.</p>
      {items.length === 0 ? (
        <p className="text-muted2 text-sm">No inventory items recorded yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clinician-inventory-grid">
          {items.map((it) => {
            const st = STOCK[it.status] || STOCK.available;
            const Icon = INV_ICON[it.type] || Package;
            return (
              <div key={it.id} data-testid="clinician-inventory-card" className="bg-white border border-line rounded-md p-5">
                <div className="flex items-start justify-between">
                  <Icon className="w-6 h-6 text-clinical" />
                  <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1.5" style={{ color: st.color, backgroundColor: st.bg }}>
                    {it.status !== "available" && (
                      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: st.color }} />
                    )}
                    {st.label}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-lg text-ink mt-3">{it.name}</h3>
                {(clinicName(it.clinic_id) || it.location) && (
                  <p className="text-sm text-muted2 mt-1 inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-clinical shrink-0" />
                    {clinicName(it.clinic_id) || it.location}
                  </p>
                )}
                <p className="font-heading font-black text-3xl text-ink mt-3">
                  {it.quantity} <span className="text-base font-medium text-muted2">{it.unit}</span>
                </p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ProfileTab() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/clinician/me"); setP(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const words = (p?.bio || "").trim() ? (p.bio.trim().split(/\s+/).length) : 0;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((prev) => ({ ...prev, picture: data.url }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (words > 100) return toast.error("Biography must be 100 words or fewer.");
    setSaving(true);
    try {
      await api.put("/clinician/profile", {
        name: p.name, title: p.title, specialty: p.specialty, bio: p.bio,
        whatsapp: p.whatsapp, email: p.email || undefined, picture: p.picture,
        languages: p.languages || [], interpreter_languages: p.interpreter_languages || [],
      });
      toast.success("Profile saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (!p) return <p className="text-muted2">Loading…</p>;
  const picSrc = p.picture ? (p.picture.startsWith("http") ? p.picture : `${BACKEND}${p.picture}`) : null;

  return (
    <form onSubmit={save} data-testid="clinician-profile-form" className="max-w-2xl space-y-4">
      <p className="text-sm text-muted2">Your profile is shown to a patient only after an appointment is scheduled with you. Your email and WhatsApp number are never shown publicly.</p>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-sand border border-line overflow-hidden flex items-center justify-center shrink-0">
          {picSrc ? <img src={picSrc} alt="" className="w-full h-full object-cover" data-testid="clinician-photo" /> : <Stethoscope className="w-8 h-8 text-muted2" />}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="clinician-photo-input" />
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="clinician-photo-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 hover:bg-clinical/5">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload photo
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">Title</span>
          <input data-testid="clinician-title" placeholder="e.g. Dr / Nurse" value={p.title || ""} onChange={(e) => setP({ ...p, title: e.target.value })} className={`${inp} mt-1.5`} /></label>
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">Full name</span>
          <input data-testid="clinician-name" required value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} className={`${inp} mt-1.5`} /></label>
      </div>
      <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">Clinical specialty</span>
        <input data-testid="clinician-specialty" placeholder="e.g. Paediatrics, Snakebite / Antivenom" value={p.specialty || ""} onChange={(e) => setP({ ...p, specialty: e.target.value })} className={`${inp} mt-1.5`} /></label>
      <label className="block">
        <span className="text-xs uppercase tracking-wider font-bold text-muted2">Biography <span className={words > 100 ? "text-critical" : "text-muted2"}>({words}/100 words)</span></span>
        <textarea data-testid="clinician-bio" rows={5} value={p.bio || ""} onChange={(e) => setP({ ...p, bio: e.target.value })} className={`${inp} mt-1.5 resize-y`} placeholder="Tell patients about your experience and approach (max 100 words)." />
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">WhatsApp number (private)</span>
          <input data-testid="clinician-whatsapp" placeholder="+263771234567" value={p.whatsapp || ""} onChange={(e) => setP({ ...p, whatsapp: e.target.value })} className={`${inp} mt-1.5`} /></label>
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">Email (private)</span>
          <input data-testid="clinician-email" type="email" value={p.email || ""} onChange={(e) => setP({ ...p, email: e.target.value })} className={`${inp} mt-1.5`} /></label>
      </div>
      <div className="border-t border-line pt-4">
        <span className="text-sm font-semibold text-ink">Languages &amp; interpreting</span>
        <p className="text-xs text-muted2 mb-3">Select the languages you speak, and mark any language for which an interpreter is required.</p>
        <LanguageFields
          languages={p.languages || []}
          interpreterLanguages={p.interpreter_languages || []}
          onChange={({ languages, interpreter_languages }) => setP({ ...p, languages, interpreter_languages })}
        />
      </div>
      <button type="submit" disabled={saving} data-testid="clinician-save-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-5 py-2.5 font-semibold hover:bg-clinicaldark transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save profile
      </button>
    </form>
  );
}

function AvailabilityTab() {
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ date: "", start_time: "", end_time: "" });
  const load = useCallback(async () => {
    try { const { data } = await api.get("/clinician/availability"); setSlots(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.date || !form.start_time || !form.end_time) return toast.error("Please fill date, start and end time.");
    try { await api.post("/clinician/availability", form); toast.success("Availability added"); setForm({ date: "", start_time: "", end_time: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/clinician/availability/${id}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-muted2 mb-4">Publish the times you are available. Scheduling staff see this on the shared calendar when booking consultations.</p>
      <form onSubmit={add} data-testid="availability-form" className="flex flex-wrap items-end gap-2 mb-6 bg-white border border-line rounded-md p-4">
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">Date</span>
          <input type="date" data-testid="availability-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${inp} mt-1.5`} /></label>
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">From</span>
          <input type="time" data-testid="availability-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={`${inp} mt-1.5`} /></label>
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">To</span>
          <input type="time" data-testid="availability-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={`${inp} mt-1.5`} /></label>
        <button type="submit" data-testid="availability-add-btn" className="inline-flex items-center gap-1.5 bg-calm text-white rounded-md px-4 py-2 font-semibold"><Plus className="w-4 h-4" /> Add slot</button>
      </form>
      <div className="grid gap-2" data-testid="availability-list">
        {slots.length === 0 && <p className="text-muted2 text-sm">No availability published yet.</p>}
        {slots.map((s) => (
          <div key={s.id} data-testid="availability-row" className="flex items-center justify-between bg-white border border-line rounded-md px-4 py-3">
            <span className="text-sm text-ink"><b>{s.date}</b> · {s.start_time}–{s.end_time}</span>
            <button onClick={() => remove(s.id)} data-testid="availability-delete-btn" className="text-critical hover:bg-critical/5 rounded-md p-1.5"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AppointmentsTab({ appts, setJoin }) {
  return (
    <div className="max-w-2xl grid gap-2" data-testid="clinician-appointments">
      {appts.length === 0 && <p className="text-muted2 text-sm">No consultations scheduled with you yet.</p>}
      {appts.map((a) => (
        <div key={a.id} data-testid="clinician-appt-row" className="bg-white border border-line rounded-md px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-heading font-bold text-ink flex items-center gap-2 flex-wrap">
              {a.public_code} — {a.patient_name}
              {a.patient_waiting && a.status === "scheduled" && (
                <span data-testid="clinician-waiting-badge" className="text-xs font-bold uppercase px-2 py-0.5 rounded bg-alert/20 text-alert animate-pulse">● Waiting</span>
              )}
            </div>
            <div className="text-sm text-muted2">{fmtAppt(a)} · {a.mode}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${a.status === "completed" ? "bg-calm/10 text-calm" : a.status === "cancelled" ? "bg-critical/10 text-critical" : "bg-clinical/10 text-clinical"}`}>{a.status}</span>
            {a.status === "scheduled" && (
              <button onClick={() => setJoin(a.id)} data-testid="clinician-join-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-clinical rounded-md px-3 py-1.5"><Video className="w-4 h-4" /> Join</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function MessagesTab({ onRead }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const scrollRef = useRef(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/me/messages"); setMsgs(data); onRead?.(); }
    catch (e) { toast.error(apiErr(e)); }
  }, [onRead]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs]);
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try { await api.post("/messages", { body }); setBody(""); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="max-w-2xl">
      <p className="text-sm text-muted2 mb-4">Private conversation with administration staff. Reminders and coordination messages appear here.</p>
      <div className="bg-white border border-line rounded-md p-4 flex flex-col">
        <div ref={scrollRef} className="max-h-96 overflow-y-auto flex flex-col gap-2 mb-3" data-testid="clinician-message-thread">
          {msgs.length === 0 && <p className="text-sm text-muted2">No messages yet. Administration staff can reach you here.</p>}
          {msgs.map((m) => (
            <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.from_staff ? "bg-sand self-start" : "bg-clinical text-white self-end"}`}>
              <div className="text-[10px] uppercase font-bold opacity-70 mb-0.5">{m.from_name}</div>
              {m.body}
              {m.created_at && <div className="text-[10px] opacity-60 mt-1 text-right" data-testid="clinician-message-time">{fmtStamp(m.created_at)}</div>}
            </div>
          ))}
        </div>
        <form onSubmit={send} className="flex gap-2">
          <input data-testid="clinician-message-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply to administration…" className={inp} />
          <button type="submit" data-testid="clinician-message-send" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 font-semibold"><Send className="w-4 h-4" /></button>
        </form>
      </div>
    </div>
  );
}

export default function ClinicianDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("profile");
  const [unread, setUnread] = useState(0);
  const [appts, setAppts] = useState([]);
  const [join, setJoin] = useState(null);
  const loadUnread = useCallback(async () => {
    try { const { data } = await api.get("/me/messages/unread"); setUnread(data.unread || 0); } catch {}
  }, []);
  const loadAppts = useCallback(async () => {
    try { const { data } = await api.get("/clinician/appointments"); setAppts(data); } catch {}
  }, []);
  useEffect(() => {
    loadUnread();
    loadAppts();
    const t = setInterval(() => { loadUnread(); loadAppts(); }, 15000);
    return () => clearInterval(t);
  }, [loadUnread, loadAppts]);
  useEffect(() => { if (tab === "messages") setUnread(0); }, [tab]);
  // Gentle chime + desktop alert the moment one of this clinician's patients checks in.
  useWaitingRing(appts, setJoin);
  return (
    <div className="min-h-screen bg-sand flex flex-col">
      <header className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" data-testid="clinician-logo-link" className="flex items-center gap-2 font-heading font-black text-xl hover:opacity-90 transition-opacity">
            <HeartPulse className="w-6 h-6 text-alert" /> Hutanohwevanhu.org
            <span className="text-white/50 text-sm font-body font-normal">Clinician</span>
          </Link>
          <div className="flex items-center gap-4">
            <PushButton userType="clinician" variant="dark" />
            <Link to="/" className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="view-public-link"><ExternalLink className="w-4 h-4" /> Public site</Link>
            <button onClick={logout} data-testid="logout-btn" className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto w-full px-4 py-8 flex-1">
        <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-1">Welcome, {user?.name}</h1>
        <p className="text-muted2 mb-6">Manage your professional profile and availability.</p>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-line">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`clinician-tab-${t.k}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.k ? "border-clinical text-clinical" : "border-transparent text-muted2 hover:text-ink"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.k === "messages" && unread > 0 && (
                <span data-testid="clinician-messages-badge" className="text-xs font-bold bg-critical text-white rounded-full px-2 min-w-[20px] text-center">{unread}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "profile" && <ProfileTab />}
        {tab === "availability" && <AvailabilityTab />}
        {tab === "appointments" && <AppointmentsTab appts={appts} setJoin={setJoin} />}
        {tab === "inventory" && <InventoryTab />}
        {tab === "messages" && <MessagesTab onRead={loadUnread} />}
        {tab === "settings" && <AccountArea />}
      </div>
      {join && <JitsiRoom appointmentId={join} onClose={() => { setJoin(null); loadAppts(); }} />}
      <Footer />
    </div>
  );
}
